import {FullScreenLoadingSpinner} from 'components/FullScreenLoadingSpinner/FullScreenLoadingSpinner';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {getValueFromSurveyData} from 'utils/getValueFromSurveyData';
import {PlanInfo} from 'utils/stripePlans';
import {useCheckoutScreenAnalytics} from 'utils/useCheckoutScreenAnalytics';
import {RiseHeader} from 'screens/InsomniaSurvey/components/RiseHeader/RiseHeader';
import {RiseClinicHero} from './components/RiseClinicHero/RiseClinicHero';
import {RiseClinicHeadline} from './components/RiseClinicHeadline/RiseClinicHeadline';
import {RiseClinicFeatureList} from './components/RiseClinicFeatureList/RiseClinicFeatureList';
import {RiseClinicSuccessBanner} from './components/RiseClinicSuccessBanner/RiseClinicSuccessBanner';
import {ClinicPaymentElement} from './components/ClinicPaymentElement/ClinicPaymentElement';
import styles from './components/ClinicPaymentElement/ClinicPaymentElement.module.css';
import {getISIScore} from 'screens/InsomniaSurvey/utils/isiScoring';
import {getInsuranceProviderName, handleScheduleAppointment, handleStartVob, sendISI} from './utils';
import {Appointment, sendSlackNotification} from 'externalAPIs/insurance-eligibility';
import {insomniaPlans} from '../survey/config';
import {SurveyData} from 'screens/InsomniaSurvey/utils/surveyData';
import {surveyInstance} from 'screens/InsomniaSurvey/InsomniaSurvey';
import Analytics from 'utils/Analytics';

type CheckoutScreenProps = {
  variantName: string;
  /**
   * Route prefix this checkout is mounted under, e.g. '/book-appointment'.
   * Defaults to '' for the top-level routes. Used for the back link to the
   * survey and the forward navigation to the confirmation screen.
   */
  basePath?: string;
};

export default function InsomniaCheckout({variantName, basePath = ''}: CheckoutScreenProps) {
  const insomniaPlan = insomniaPlans.US as PlanInfo;
  const surveyPath = `${basePath}/start`;
  const confirmationPath = `${basePath}/appointment-confirmation`;

  const [planToPurchase] = useState<PlanInfo>(insomniaPlan);
  const paymentRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState<string | undefined>(undefined);
  const [subscription, setSubscription] = useState<string | undefined>(undefined);

  const [error, setError] = useState<string | undefined>(undefined);

  useCheckoutScreenAnalytics(variantName);

  // Mark that we're on the checkout page and handle unmount
  useEffect(() => {
    sessionStorage.setItem('insomnia-on-checkout', 'true');
    return () => {
      // When component unmounts (user navigates away), check if going to survey
      // This catches both UI back button and browser back button
      sessionStorage.removeItem('insomnia-on-checkout');
      // Use setTimeout to check destination after navigation
      setTimeout(() => {
        const currentPath = window.location.pathname;
        if (currentPath.startsWith(surveyPath)) {
          sessionStorage.setItem('insomnia-navigating-back-from-checkout', 'true');
          // Reset survey completion state in saved data
          // Don't clear the instance here - let the survey component handle it properly
          const surveyData = SurveyData.read();
          if (surveyData) {
            surveyData.completed = false;
            // Ensure pageNo is preserved
            if (surveyInstance.state === 'completed' && surveyData.pageNo === undefined) {
              // If pageNo wasn't saved, try to get it from the instance before it's cleared
              surveyData.pageNo = surveyInstance.currentPageNo;
            }
            window.localStorage.setItem('rise-survey-data', JSON.stringify(surveyData));
          }
        }
      }, 0);
    };
  }, [surveyPath]);

  // Function to reset survey completion state (used by UI back button)
  const resetSurveyCompletionState = useCallback(() => {
    // Mark that we're navigating back from checkout
    sessionStorage.setItem('insomnia-navigating-back-from-checkout', 'true');

    // Reset survey completion state to allow editing
    const surveyData = SurveyData.read();
    if (surveyData) {
      surveyData.completed = false;
      window.localStorage.setItem('rise-survey-data', JSON.stringify(surveyData));
    }
    if (surveyInstance.state === 'completed') {
      surveyInstance.clear();
    }
  }, []);

  const handleSubscriptionSuccess = useCallback(
    async (subscription: string) => {
      const personId = getValueFromSurveyData('person_id');
      const appointmentStartTime = getValueFromSurveyData('appointment_start_time');
      const appointmentEndTime = getValueFromSurveyData('appointment_end_time');
      const appointmentProviderId = getValueFromSurveyData('appointment_provider_id');
      // schedule appointment
      setLoading(true);
      setLoadingMessage('Scheduling appointment...');
      let appointment: Appointment | undefined;
      try {
        const result = await handleScheduleAppointment();
        if (result.success) {
          appointment = result.appointment;
          Analytics.track('Clinic Booking Completed', {
            appointmentId: appointment?.appointmentId,
            patientId: appointment?.patientId,
            providerId: appointmentProviderId,
            providerName: appointment?.providerName,
            startTime: appointmentStartTime,
            endTime: appointmentEndTime,
            personId,
          });
        } else {
          Analytics.track('Clinic Booking Failed', {
            reason: result.error ?? 'unknown',
            personId,
            providerId: appointmentProviderId,
            startTime: appointmentStartTime,
          });
          setError('Error scheduling appointment. Please try again.');
          setLoading(false);
          return;
        }
      } catch (error) {
        Analytics.track('Clinic Booking Failed', {
          reason: (error as Error).message,
          personId,
          providerId: appointmentProviderId,
          startTime: appointmentStartTime,
        });
        console.error('Error scheduling appointment:', error);
        setError('Error scheduling appointment. Please try again.');
        setLoading(false);
        return;
      }

      const isiScore = getISIScore();
      const patientId = appointment?.patientId;

      if (patientId && personId && isiScore) {
        try {
          await sendISI(personId, patientId, isiScore);
        } catch (error) {
          console.error('Error sending ISI:', error);
          // Don't fail the checkout in this case
        }
      }

      if (patientId && personId && isiScore) {
        try {
          // don't await this, we are just kicking it off on the backend
          handleStartVob();
        } catch (error) {
          console.error('Error starting VOB:', error);
          // Don't fail the checkout in this case
        }
      }

      // Send Slack notification with survey results
      try {
        const surveyData = SurveyData.read();
        await sendSlackNotification(surveyData, appointment);
      } catch (error) {
        console.error('Error sending Slack notification:', error);
        // Don't fail the checkout if Slack notification fails
      }

      setSubscription(subscription);
      setLoading(false);

      // navigate to success screen
      navigate(confirmationPath);
    },
    [navigate, confirmationPath],
  );

  useEffect(() => {
    if (subscription) {
      navigate(confirmationPath);
    }
  }, [navigate, subscription, confirmationPath]);

  const providerValue = getValueFromSurveyData('insurance_coverage');
  const providerName = getInsuranceProviderName(providerValue);

  return (
    <div className={styles.container} data-testid="screen-checkout">
      {loading ? <FullScreenLoadingSpinner message={loadingMessage} /> : null}

      <RiseHeader
        showLogo
        backButton
        onBackClick={() => {
          resetSurveyCompletionState();
          navigate(surveyPath);
        }}
      />

      <RiseClinicHero />
      <RiseClinicHeadline />
      <RiseClinicFeatureList />
      <RiseClinicSuccessBanner />

      <div className={styles.disclaimer}>
        <span className={styles.asterisk}>*</span>Sessions estimate is based on {providerName} copays and deductibles
        apply. To confirm your card, we'll apply a $0.50 hold that will disappear within seven days.
      </div>

      {error ? <p style={{color: 'red'}}>{error}</p> : null}

      <div ref={paymentRef} style={{margin: '0 24px 10rem'}}>
        <ClinicPaymentElement
          onSuccess={handleSubscriptionSuccess}
          planToPurchase={planToPurchase}
          onLoadingChanged={isLoading => {
            if (isLoading) {
              setLoadingMessage('Processing payment...');
            }
            setLoading(isLoading);
          }}
        />
      </div>
    </div>
  );
}
